import { LogIn } from 'lucide-react';

import type { GuideModule } from '../types';

/**
 * `proposal.md` § "Cobertura Inicial": login, 2FA/TOTP, navegación básica,
 * perfil/logout, campana de notificaciones y separación con el portal del
 * cliente.
 */
export const ONBOARDING_MODULE: GuideModule = {
  id: 'onboarding',
  title: 'Primeros pasos',
  icon: LogIn,
  requiredPermission: null,
  priority: 0,
  summary:
    'Esta sección te ayuda a arrancar: cómo entrar al sistema, cómo activar la verificación en dos pasos que te van a pedir la primera vez, cómo moverte entre los módulos del menú lateral y cómo cerrar sesión cuando termines. No hace falta que sepas nada técnico: seguí los pasos en orden y en un rato ya vas a estar usando el sistema con confianza. Si en algún momento no encontrás algo, la barra de búsqueda de esta Guía busca por palabra clave en todos los módulos.',
  steps: [
    {
      title: 'Iniciá sesión con tu correo y tu contraseña',
      description:
        'Entrá a la pantalla de inicio, completá "Correo electrónico" y "Contraseña" y hacé clic en "Ingresar".',
      keyTerms: ['sesion', 'correo-electronico', 'contrasena'],
    },
    {
      title: 'Configurá la verificación en dos pasos la primera vez',
      description:
        'Si todavía no la tenés activa, el sistema te va a pedir un "Código de verificación" de seis dígitos. Andá a Cuenta / Seguridad, escaneá el código QR con tu aplicación de autenticación y completá el código para confirmar.',
      keyTerms: ['verificacion-en-dos-pasos', 'codigo-qr'],
    },
    {
      title: 'Recorré el menú lateral',
      description:
        'A la izquierda vas a ver los módulos a los que tenés acceso, agrupados en 4 bloques: "Operación diaria" (Tablero del día, Pedidos, Producción, Remitos), "Facturación", "Maestros" (Clientes, Contratos, Catálogo, Calendario) y "Configuración" (visible si tenés al menos uno de los permisos que habilitan alguna de sus secciones: datos fiscales, parámetros del sistema, usuarios o roles). Un grupo entero desaparece si tu rol no tiene acceso a ninguno de sus módulos. "Guía" queda fijo al pie del menú, fuera de estos grupos, siempre visible. Hacé clic en cualquier módulo para entrar.',
    },
    {
      title: 'Encontrá dónde aterrizás al iniciar sesión',
      description:
        'Después de iniciar sesión no siempre vas a caer en el Tablero del día: cada rol tiene su propio punto de partida. Mirá "Primeros pasos por rol" en la sección Roles y permisos de esta Guía para saber dónde vas a aterrizar vos y qué esperar ahí.',
      keyTerms: ['rol'],
    },
    {
      title: 'Fijate en la campana de notificaciones',
      description:
        'Arriba a la derecha, junto a tu nombre, la campana muestra un número cuando tenés notificaciones sin leer: confirmaciones de pedido, cierres de corte, solicitudes de cambio y más. Hacé clic para abrir el panel con las más recientes.',
      keyTerms: ['notificacion-in-app'],
    },
    {
      title: 'Consultá o gestioná según el módulo',
      description:
        'Cada módulo tiene su propia guía detallada, listada en el índice de esta Guía: Clientes, Contratos, Catálogo, Calendario, Pedidos, Producción y Notificaciones.',
    },
    {
      title: 'Revisá "Cuenta / Seguridad" cuando lo necesites',
      description:
        'Hacé clic en tu nombre arriba a la derecha y elegí "Cuenta / Seguridad" para configurar tu verificación en dos pasos o revisar tus preferencias de notificación.',
    },
    {
      title: 'Recuperá tu contraseña si la olvidaste',
      description:
        'En la pantalla de inicio hacé clic en "¿Olvidaste tu contraseña?", escribí tu correo y enviá el formulario. El sistema siempre muestra el mismo aviso, exista o no esa cuenta. Si la cuenta existe, te llega un correo con un enlace: abrilo, elegí una contraseña nueva de al menos 10 caracteres y confirmala. El enlace vale por tiempo limitado.',
    },
    {
      title: 'Cerrá sesión cuando termines',
      description:
        'Hacé clic en tu nombre arriba a la derecha y elegí "Cerrar sesión". Es buena práctica hacerlo si usás una computadora compartida.',
    },
  ],
  states: [],
  faq: [
    {
      question: '¿Qué significa el aviso amarillo "Tu rol exige verificación en dos pasos"?',
      answer:
        'Tu rol exige verificación en dos pasos y todavía no la configuraste. Hacé clic en "Configurar verificación" dentro del aviso, o entrá a Cuenta / Seguridad, para completarla. Es un aviso compacto de una sola línea: podés cerrarlo con la × sin configurar nada, pero vuelve a aparecer en tu próxima sesión hasta que actives la verificación en dos pasos.',
    },
    {
      question: '¿Las empresas clientes inician sesión en esta misma pantalla?',
      answer:
        'No. Las empresas y sus contactos inician sesión en el portal del cliente, una dirección separada de este sistema interno. Si un contacto de una empresa te pide ayuda para entrar, fijate primero que esté usando la dirección del portal, no esta.',
    },
    {
      question: '¿Por qué me pide un código de seis dígitos además de la contraseña?',
      answer:
        'Es la verificación en dos pasos: una capa extra de seguridad para que, aunque alguien consiga tu contraseña, no pueda entrar sin tu teléfono.',
    },
    {
      question: '¿Qué aplicación necesito para generar el código?',
      answer:
        'Cualquier aplicación de autenticación compatible con códigos TOTP, como Google Authenticator o Authy. Escaneás el código QR una sola vez y desde ahí genera un código nuevo cada 30 segundos.',
    },
    {
      question: 'Perdí mi teléfono y no puedo generar el código, ¿qué hago?',
      answer: 'Pedile a un administrador que te ayude a reconfigurar la verificación en dos pasos.',
    },
    {
      question: 'Pedí recuperar mi contraseña y no me llega el correo, ¿qué hago?',
      answer:
        'Revisá la carpeta de spam y que hayas escrito bien tu correo; el aviso es el mismo aunque la cuenta no exista. Si el enlace venció, pedí uno nuevo desde "¿Olvidaste tu contraseña?". Si nunca llega, puede ser que el correo saliente no esté configurado: en ese caso no se envía ningún mensaje y un administrador con permiso tiene que configurarlo en Configuración > "Correo saliente".',
    },
    {
      question: '¿Por qué no veo todos los módulos del menú?',
      answer:
        'El menú muestra solo los módulos a los que tu rol tiene acceso. Si te falta alguno que necesitás, pedile a un administrador que revise tus permisos.',
    },
  ],
  tips: [
    'Activá la verificación en dos pasos apenas puedas: es rápido y evita bloqueos por seguridad más adelante.',
    'Si te vas a ausentar de tu computadora, cerrá sesión en vez de dejarla abierta.',
    'Usá la barra de búsqueda de esta Guía si no encontrás algo: busca por palabra clave, sin importar mayúsculas o acentos.',
    'Guardá el código QR o el secreto que te muestra la pantalla de Seguridad en un lugar seguro por si cambiás de teléfono.',
    'Desde el celular, cada pantalla con varios botones de encabezado muestra solo la acción principal a la vista; el resto queda agrupado detrás de "Más acciones", junto al botón principal.',
  ],
};
